import React, { useState, useEffect } from 'react';
import {
  MicrophoneIcon,
  ArrowPathIcon,
  PhoneIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  InformationCircleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { api, API_BASE_URL, getAuthToken } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const SendVoicePage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [chatId, setChatId] = useState('');
  const [voiceFile, setVoiceFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getWhatsAppClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching WhatsApp clients:', error);
      toast.error('Failed to load WhatsApp clients');
    } finally {
      setLoading(false);
    }
  };

  // Prepare WhatsApp clients data for SearchableSelect
  const agentOptions = agents && Object.keys(agents).length > 0
    ? Object.values(agents)
        .filter(client => client.status === 'active')
        .map((client) => ({
          value: client.id,
          label: client.agent_name || client.display_name || `Client ${client.client_id}`,
          description: client.whatsapp_number || client.client_id,
          icon: '📱',
          port: client.port,
          webhookApiKey: client.webhook_api_key || client.api_key,
          clientId: client.client_id
        }))
    : [];


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/x-m4a'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid audio file (MP3, WAV, OGG, M4A)');
        return;
      }

      // Validate file size (16MB max)
      const maxSize = 16 * 1024 * 1024; // 16MB in bytes
      if (file.size > maxSize) {
        toast.error('File size must be less than 16MB');
        return;
      }

      // Store voice file locally for upload on submit (like images)
      setVoiceFile({
        actualFile: file, // Store the actual file for upload on submit
        filename: file.name,
        mimetype: file.type,
        size: file.size
      });
      toast.success('Voice file selected successfully!');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
        const file = new File([blob], `recording_${Date.now()}.ogg`, { type: 'audio/ogg; codecs=opus' });
        // Store recorded voice file locally for upload on submit (like images)
        setVoiceFile({
          actualFile: file, // Store the actual file for upload on submit
          filename: file.name,
          mimetype: file.type,
          size: file.size
        });
        toast.success('Recording saved successfully!');

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      setRecordedChunks(chunks);
      recorder.start();
      setIsRecording(true);
      toast.info('Recording started...');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      toast.info('Recording stopped');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        return {
          url: result.url,
          mimetype: result.mimetype
        };
      } else {
        throw new Error('Failed to upload file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleSendVoice = async () => {
    if (!selectedAgent || !voiceFile || !chatId) {
      toast.error('Please fill in all fields and select a voice file');
      return;
    }

    setSending(true);
    try {
      const client = Object.values(agents).find(a => a.id === selectedAgent);
      if (!client) {
        toast.error('WhatsApp client not found');
        return;
      }

      let voiceUrl;
      
      // Upload voice file now (on submit, not on selection)
      if (voiceFile.url) {
        // Already uploaded URL
        voiceUrl = voiceFile.url;
      } else if (voiceFile.actualFile) {
        // Upload the actual file now
        const formData = new FormData();
        formData.append('file', voiceFile.actualFile);

        const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081'}/upload/public-voice`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          const error = await uploadResponse.json();
          throw new Error(error.error || 'Failed to upload voice file');
        }

        const uploadResult = await uploadResponse.json();
        voiceUrl = uploadResult.url;
      } else {
        throw new Error('No voice file available');
      }

      // Send voice message using the uploaded URL
      const response = await api.sendVoiceMessage(
        client.client_id,
        chatId,
        voiceUrl
      );

      toast.success('Voice message sent successfully!');
      setVoiceFile(null);
      setChatId('');
      // Clear the file input
      const fileInput = document.getElementById('voice-file-input');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error sending voice message:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(error.message || 'Failed to send voice message');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your agents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <MicrophoneIcon className="h-8 w-8 text-orange-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Send Voice Message</h1>
                  <p className="text-sm text-gray-600">Send audio messages to WhatsApp contacts</p>
                </div>
              </div>
              <button
                onClick={fetchAgents}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <MicrophoneIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Voice Message Details</h2>
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSendVoice(); }} className="px-6 py-6 space-y-6">

            {/* Agent Selection */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select WhatsApp Agent
              </h3>

              <SearchableSelect
                label="WhatsApp Agent"
                name="client"
                options={agentOptions}
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                placeholder="Search and select a WhatsApp agent..."
                isRequired={true}
                isSearchable={true}
                isClearable={true}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">WhatsApp Agent Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a connected WhatsApp agent to send your voice message. Only agents with active connections are available.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Voice Message Details */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Message Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Chat ID <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                    placeholder="233554694531"
                    required
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Enter phone number in international format (without +)
                </p>
              </div>

              {/* Voice File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Voice File <span className="text-red-500">*</span>
                </label>

                {/* Recording Controls */}
                <div className="mb-4 flex space-x-3">
                  {!isRecording ? (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      <MicrophoneIcon className="h-4 w-4 mr-2" />
                      Record Voice
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors animate-pulse"
                    >
                      <MicrophoneIcon className="h-4 w-4 mr-2" />
                      Recording... Click to Stop
                    </button>
                  )}
                </div>

                {/* File Upload Area - Only show if no voice file is selected */}
                {!voiceFile && (
                  <div className="relative border-2 border-dashed rounded-lg p-6 transition-colors hover:border-whatsapp-400 focus-within:border-whatsapp-500">
                    <div className="text-center">
                      <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="voice-file-input" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Or upload an existing audio file
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            MP3, WAV, OGG, M4A up to 16MB
                          </span>
                        </label>
                        <input
                          id="voice-file-input"
                          name="voice-file"
                          type="file"
                          accept="audio/*"
                          className="sr-only"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Selected File Display */}
                {voiceFile && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <DocumentIcon className="h-5 w-5 text-green-600 mr-3" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">{voiceFile.filename || voiceFile.name}</p>
                        <p className="text-xs text-green-600">{formatFileSize(voiceFile.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setVoiceFile(null);
                          const fileInput = document.getElementById('voice-file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                        className="text-green-600 hover:text-green-800"
                      >
                        <ArrowPathIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Supported Formats:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• MP3 - MPEG Audio Layer III</li>
                    <li>• WAV - Waveform Audio File Format</li>
                    <li>• OGG - Ogg Vorbis</li>
                    <li>• M4A - MPEG-4 Audio</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-2">
                    Maximum file size: 16MB
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setSelectedAgent('');
                  setVoiceFile(null);
                  setChatId('');
                  const fileInput = document.getElementById('voice-file-input');
                  if (fileInput) fileInput.value = '';
                }}
                disabled={sending}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>

              <button
                type="submit"
                disabled={!selectedAgent || !voiceFile || !chatId || sending}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {sending ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Sending Voice...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Send Voice Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SendVoicePage;